import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { v4 as uuidv4 } from "uuid";

import {
  createBillReappropriationAnalysis,
  fetchAiAccess,
  fetchAiAnalysis,
  fetchBillReappropriationHistory,
  submitAiFeedback,
} from "../ai/aiApi";

const POLL_DELAYS_MS = [750, 1500, 3000];

const quotaLabel = (quota, period) => {
  if (!quota) return "Cota indisponível";
  if (quota.unlimited) return "Uso ilimitado";
  if (quota.remaining === 0) return "Cota utilizada neste período";
  return `${quota.remaining} ${
    quota.remaining === 1 ? "análise restante" : "análises restantes"
  } ${period === "monthly" ? "neste mês" : "nesta semana"}`;
};

const percentage = (bps) => {
  if (!Number.isInteger(bps)) return "Percentual indisponível";
  return `${(bps / 100).toLocaleString("pt-BR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}%`;
};

const outputOf = (analysis) =>
  analysis?.output || analysis?.result || analysis || {};

const isProcessing = (analysis) =>
  analysis?.state === "reserved" || analysis?.status === "processing";

const terminalFailure = (analysis) => {
  if (analysis?.state !== "failed") return null;
  if (
    analysis?.status === "provider_unavailable"
    || analysis?.errorCode === "ai_provider_unavailable"
    || analysis?.errorCode === "ai_provider_invalid_output"
  ) {
    return {
      code: "provider_unavailable",
      message: "O provedor de IA está temporariamente indisponível. Esta tentativa não consumiu sua cota. Tente novamente.",
    };
  }
  return {
    code: "analysis_failed",
    message: "Não foi possível concluir esta análise. A tentativa não consumiu sua cota; tente novamente.",
  };
};

const historyLabel = (analysis) => {
  if (analysis?.state === "completed") return "Sugestão concluída";
  if (analysis?.state === "failed") {
    return analysis?.status === "provider_unavailable"
      ? "Falha temporária"
      : "Falha na análise";
  }
  if (analysis?.state === "reserved") return "Em processamento";
  if (analysis?.state === "cancelled") return "Análise cancelada";
  return "Status indisponível";
};

const latestTerminalAnalysis = (analyses) =>
  (analyses || []).find((item) => item?.analysisId && item?.state === "completed") || null;

const uniqueCurrentUnit = (currentUnitId, currentAppropriation) => {
  if (Number.isInteger(currentUnitId)) return currentUnitId;
  const ids = [
    ...new Set(
      (currentAppropriation || [])
        .map((line) => line?.buildingUnitId)
        .filter(Number.isInteger),
    ),
  ];
  return ids.length === 1 ? ids[0] : null;
};

const listMessage = (values, empty, unavailable) => {
  if (values === null) return unavailable;
  if (!Array.isArray(values) || values.length === 0) return empty;
  return null;
};

const normalizedQuestion = (value) => String(value || "").trim().replace(/\s+/g, " ");
const validLine = (line) => line
  && typeof line.candidateRef === "string"
  && line.candidateRef
  && line.buildingId !== undefined
  && Number.isInteger(line.buildingUnitId)
  && typeof line.wbsCode === "string"
  && line.wbsCode
  && Number.isInteger(line.percentageBps)
  && line.percentageBps >= 0
  && typeof line.path === "string"
  && line.path;
const validSuggestions = (analysis) => {
  const suggestions = outputOf(analysis).suggestions;
  if (!Array.isArray(suggestions) || suggestions.length > 3) return null;
  if (!suggestions.every((suggestion) => suggestion
    && typeof suggestion.suggestionId === "string"
    && Array.isArray(suggestion.lines)
    && suggestion.lines.length >= 1
    && suggestion.lines.length <= 10
    && suggestion.lines.every(validLine)
    && suggestion.lines.reduce((sum, line) => sum + line.percentageBps, 0) === 10000)) return null;
  return suggestions;
};

const BillReappropriationAiPanel = ({
  workId,
  billId,
  availableUnits = [],
  currentUnitId,
  currentAppropriation = [],
  onApply,
  readOnly = false,
  appliedSuggestionKey = null,
  compactByDefault = false,
}) => {
  const scopeKey = `${workId}:${billId}`;
  const titleRef = useRef(null);
  const mountedRef = useRef(false);
  const scopeRef = useRef({ key: scopeKey, generation: 0 });
  const intentRef = useRef({ fingerprint: scopeKey, generation: 0 });
  const intentScopeRef = useRef(scopeKey);
  const currentAnalysisIdRef = useRef(null);
  if (scopeRef.current.key !== scopeKey) {
    scopeRef.current = {
      key: scopeKey,
      generation: scopeRef.current.generation + 1,
    };
  }
  const loadControllerRef = useRef(null);
  const requestControllerRef = useRef(null);
  const pollControllerRef = useRef(null);
  const pollTimerRef = useRef(null);
  const feedbackControllerRef = useRef(null);
  const feedbackPendingRef = useRef(null);
  const reconcileControllerRef = useRef(null);
  const requestActiveRef = useRef(false);
  const idempotencyKeyRef = useRef(null);
  const applyGuardRef = useRef(appliedSuggestionKey);
  const appliedPropRef = useRef(appliedSuggestionKey);
  if (appliedPropRef.current !== appliedSuggestionKey) {
    appliedPropRef.current = appliedSuggestionKey;
    applyGuardRef.current = appliedSuggestionKey;
  }
  const initialUnit = uniqueCurrentUnit(currentUnitId, currentAppropriation);
  const availableUnitKey = availableUnits.map((unit) => unit.id).sort().join(",");
  const unitIds = useMemo(
    () => new Set(availableUnits.map((unit) => unit.id)),
    [availableUnits],
  );
  const [selectedUnits, setSelectedUnits] = useState(() =>
    unitIds.has(initialUnit) ? [initialUnit] : [],
  );
  const [question, setQuestion] = useState("");
  const [mode, setMode] = useState("single");
  const [access, setAccess] = useState(null);
  const [quota, setQuota] = useState(null);
  const [analysis, setAnalysis] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [polling, setPolling] = useState(false);
  const [pollPaused, setPollPaused] = useState(false);
  const [feedbackAnalysisId, setFeedbackAnalysisId] = useState(null);
  const [feedbackPendingAnalysisId, setFeedbackPendingAnalysisId] = useState(null);
  const [expanded, setExpanded] = useState(!compactByDefault);
  currentAnalysisIdRef.current = analysis?.analysisId || null;

  useEffect(() => {
    setExpanded(!compactByDefault);
  }, [scopeKey, compactByDefault]);

  const canonicalUnits = [...selectedUnits].filter((id) => unitIds.has(id)).sort((a, b) => a - b);
  const fingerprint = `${scopeKey}|${normalizedQuestion(question)}|${mode}|${canonicalUnits.join(",")}`;
  if (intentRef.current.fingerprint !== fingerprint) {
    intentRef.current = { fingerprint, generation: intentRef.current.generation + 1 };
  }

  const scopeToken = () => ({ ...scopeRef.current });
  const token = () => ({ ...scopeRef.current, intent: { ...intentRef.current } });
  const current = (value) =>
    mountedRef.current &&
    scopeRef.current.key === value.key &&
    scopeRef.current.generation === value.generation &&
    (!value.intent || (intentRef.current.fingerprint === value.intent.fingerprint && intentRef.current.generation === value.intent.generation));
  const commit = (value, write) => {
    if (!current(value)) return false;
    write();
    return true;
  };
  const abortRequests = () => {
    loadControllerRef.current?.abort();
    requestControllerRef.current?.abort();
    pollControllerRef.current?.abort();
    feedbackControllerRef.current?.abort();
    reconcileControllerRef.current?.abort();
    if (pollTimerRef.current) clearTimeout(pollTimerRef.current);
    loadControllerRef.current = null;
    requestControllerRef.current = null;
    pollControllerRef.current = null;
    feedbackControllerRef.current = null;
    feedbackPendingRef.current = null;
    reconcileControllerRef.current = null;
    pollTimerRef.current = null;
    requestActiveRef.current = false;
  };

  useLayoutEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      scopeRef.current = {
        ...scopeRef.current,
        generation: scopeRef.current.generation + 1,
      };
      abortRequests();
    };
  }, []);

  useEffect(() => {
    const nextInitial = uniqueCurrentUnit(currentUnitId, currentAppropriation);
    setSelectedUnits(unitIds.has(nextInitial) ? [nextInitial] : []);
    setQuestion("");
    setMode("single");
    setFeedbackAnalysisId(null);
  }, [scopeKey]);

  useLayoutEffect(() => {
    const scopeChanged = intentScopeRef.current !== scopeKey;
    intentScopeRef.current = scopeKey;
    requestControllerRef.current?.abort();
    pollControllerRef.current?.abort();
    reconcileControllerRef.current?.abort();
    if (pollTimerRef.current) clearTimeout(pollTimerRef.current);
    requestControllerRef.current = null;
    pollControllerRef.current = null;
    reconcileControllerRef.current = null;
    pollTimerRef.current = null;
    requestActiveRef.current = false;
    idempotencyKeyRef.current = null;
    feedbackControllerRef.current?.abort();
    feedbackControllerRef.current = null;
    feedbackPendingRef.current = null;
    setSubmitting(false);
    setPolling(false);
    setPollPaused(false);
    if (scopeChanged) setHistory([]);
    setAnalysis((currentAnalysis) => {
      if (scopeChanged) return null;
      if (currentAnalysis && !isProcessing(currentAnalysis)) return currentAnalysis;
      return latestTerminalAnalysis(history);
    });
    setError(null);
    setFeedbackAnalysisId(null);
    setFeedbackPendingAnalysisId(null);
  }, [fingerprint]);

  useLayoutEffect(() => {
    feedbackControllerRef.current?.abort();
    feedbackControllerRef.current = null;
    feedbackPendingRef.current = null;
    setFeedbackAnalysisId(null);
    setFeedbackPendingAnalysisId(null);
  }, [analysis?.analysisId]);

  useEffect(() => {
    const nextInitial = uniqueCurrentUnit(currentUnitId, currentAppropriation);
    setSelectedUnits((ids) => {
      const retained = ids.filter((id) => unitIds.has(id));
      const next = retained.length ? retained : (unitIds.has(nextInitial) ? [nextInitial] : []);
      return next.length === ids.length && next.every((id, index) => id === ids[index]) ? ids : next;
    });
  }, [availableUnitKey, initialUnit]);

  useEffect(() => {
    titleRef.current?.focus({ preventScroll: true });
  }, [scopeKey]);

  const load = () => {
    const requestToken = scopeToken();
    abortRequests();
    const controller = new AbortController();
    loadControllerRef.current = controller;
    commit(requestToken, () => {
      setLoading(true);
      setError(null);
      setAccess(null);
      setQuota(null);
      setAnalysis(null);
      setHistory([]);
      setPollPaused(false);
    });
    Promise.all([
      fetchAiAccess(workId, { signal: controller.signal }),
      fetchBillReappropriationHistory(
        workId,
        { billId },
        { signal: controller.signal },
      ),
    ])
      .then(async ([nextAccess, payload]) => {
        let analyses = Array.isArray(payload?.analyses)
          ? payload.analyses
          : [];
        const latestCompleted = latestTerminalAnalysis(analyses);
        if (
          latestCompleted
          && !Object.prototype.hasOwnProperty.call(latestCompleted, "output")
        ) {
          const hydratedPayload = await fetchAiAnalysis(
            workId,
            latestCompleted.analysisId,
            { signal: controller.signal },
          );
          const hydrated = hydratedPayload?.analysis;
          if (
            !hydrated
            || hydrated.analysisId !== latestCompleted.analysisId
            || !Object.prototype.hasOwnProperty.call(hydrated, "output")
          ) {
            const historyError = new Error("Não foi possível carregar a análise concluída.");
            historyError.code = "invalid_history";
            throw historyError;
          }
          analyses = analyses.map((item) => (
            item?.analysisId === hydrated.analysisId ? hydrated : item
          ));
        }
        commit(requestToken, () => {
          setAccess(nextAccess);
          setQuota(nextAccess?.quota || null);
          setHistory(analyses);
          setAnalysis(isProcessing(analyses[0])
            ? analyses[0]
            : latestTerminalAnalysis(analyses));
        });
      })
      .catch((caught) => {
        if (caught?.code !== "request_cancelled")
          commit(requestToken, () => setError(caught));
      })
      .finally(() => {
        commit(requestToken, () => {
          if (loadControllerRef.current === controller)
            loadControllerRef.current = null;
          setLoading(false);
        });
      });
  };

  useEffect(() => {
    load();
    return () => loadControllerRef.current?.abort();
  }, [scopeKey]);

  const appendAnalysis = (nextAnalysis) => {
    setAnalysis(nextAnalysis);
    setHistory((items) => [
      nextAnalysis,
      ...items.filter((item) => item?.analysisId !== nextAnalysis?.analysisId),
    ]);
  };

  const reconcileAccess = async (requestToken) => {
    reconcileControllerRef.current?.abort();
    const controller = new AbortController();
    reconcileControllerRef.current = controller;
    try {
      const nextAccess = await fetchAiAccess(workId, { signal: controller.signal });
      commit(requestToken, () => {
        setAccess(nextAccess);
        setQuota(nextAccess?.quota || null);
      });
    } catch (_) {
      // The next open reloads quota; do not create another suggestion here.
    } finally {
      if (reconcileControllerRef.current === controller) reconcileControllerRef.current = null;
    }
  };

  useEffect(() => {
    if (!isProcessing(analysis) || !analysis?.analysisId) return undefined;
    const requestToken = token();
    const controller = new AbortController();
    let cancelled = false;
    let delayIndex = 0;
    pollControllerRef.current?.abort();
    pollControllerRef.current = controller;
    const poll = async () => {
      if (cancelled || !current(requestToken) || controller.signal.aborted)
        return;
      commit(requestToken, () => setPolling(true));
      try {
        const payload = await fetchAiAnalysis(workId, analysis.analysisId, {
          signal: controller.signal,
        });
        if (!current(requestToken) || cancelled) return;
        const nextAnalysis = payload?.analysis;
        if (nextAnalysis) {
          commit(requestToken, () => {
            appendAnalysis(nextAnalysis);
            if (payload?.quota) setQuota(payload.quota);
            const failure = terminalFailure(nextAnalysis);
            if (failure) setError(failure);
          });
          if (!isProcessing(nextAnalysis)) {
            if (idempotencyKeyRef.current?.fingerprint === requestToken.intent.fingerprint)
              idempotencyKeyRef.current = null;
            if (!payload?.quota) await reconcileAccess(requestToken);
            return;
          }
        }
      } catch (caught) {
        if (caught?.code !== "request_cancelled" && current(requestToken)) {
          commit(requestToken, () => setError(caught));
        }
        if (delayIndex >= POLL_DELAYS_MS.length) {
          commit(requestToken, () => setPollPaused(true));
          return;
        }
      } finally {
        commit(requestToken, () => setPolling(false));
      }
      if (delayIndex >= POLL_DELAYS_MS.length) {
        commit(requestToken, () => setPollPaused(true));
        return;
      }
      pollTimerRef.current = setTimeout(poll, POLL_DELAYS_MS[delayIndex]);
      delayIndex += 1;
    };
    poll();
    return () => {
      cancelled = true;
      controller.abort();
      if (pollTimerRef.current) clearTimeout(pollTimerRef.current);
      pollTimerRef.current = null;
    };
  }, [scopeKey, analysis?.analysisId, analysis?.state, analysis?.status]);

  const refreshProcessing = async () => {
    if (!isProcessing(analysis) || polling || !analysis?.analysisId) return;
    const requestToken = token();
    const controller = new AbortController();
    pollControllerRef.current?.abort();
    pollControllerRef.current = controller;
    commit(requestToken, () => {
      setPolling(true);
      setPollPaused(false);
    });
    try {
      const payload = await fetchAiAnalysis(workId, analysis.analysisId, {
        signal: controller.signal,
      });
      if (!current(requestToken) || controller.signal.aborted) return;
      const nextAnalysis = payload?.analysis;
      if (nextAnalysis) {
        const appended = commit(requestToken, () => {
          appendAnalysis(nextAnalysis);
          if (payload?.quota) setQuota(payload.quota);
          const failure = terminalFailure(nextAnalysis);
          if (failure) setError(failure);
        });
        if (!appended) return;
        if (!isProcessing(nextAnalysis)) {
          if (idempotencyKeyRef.current?.fingerprint === requestToken.intent.fingerprint)
            idempotencyKeyRef.current = null;
          if (!payload?.quota) await reconcileAccess(requestToken);
        }
      }
    } catch (caught) {
      if (caught?.code !== "request_cancelled")
        commit(requestToken, () => setError(caught));
    } finally {
      if (pollControllerRef.current === controller) pollControllerRef.current = null;
      commit(requestToken, () => setPolling(false));
    }
  };

  const exhausted = quota?.unlimited === false && quota?.remaining === 0;
  const validQuestion =
    question.trim().length >= 10 && question.trim().length <= 1000;
  const canSubmit =
    access?.enabled &&
    !readOnly &&
    !exhausted &&
    !submitting &&
    !isProcessing(analysis) &&
    validQuestion &&
    canonicalUnits.length >= 1 &&
    canonicalUnits.length <= 3;

  const submit = async () => {
    const requestToken = token();
    if (!current(requestToken) || !canSubmit || requestActiveRef.current)
      return;
    requestActiveRef.current = true;
    const controller = new AbortController();
    requestControllerRef.current = controller;
    if (idempotencyKeyRef.current?.fingerprint !== requestToken.intent.fingerprint) {
      idempotencyKeyRef.current = { fingerprint: requestToken.intent.fingerprint, key: uuidv4() };
    }
    const requestKey = idempotencyKeyRef.current;
    const ownsRequestKey = () =>
      current(requestToken) &&
      requestControllerRef.current === controller &&
      idempotencyKeyRef.current === requestKey;
    commit(requestToken, () => {
      setSubmitting(true);
      setError(null);
    });
    try {
      const result = await createBillReappropriationAnalysis(
        workId,
        {
          billId,
          question: normalizedQuestion(question),
          mode,
          buildingUnitIds: canonicalUnits,
        },
        {
          idempotencyKey: requestKey.key,
          signal: controller.signal,
        },
      );
      if (!ownsRequestKey()) return;
      const nextAnalysis = result?.analysis;
      const nextSuggestions = validSuggestions(nextAnalysis);
      const failure = terminalFailure(nextAnalysis);
      commit(requestToken, () => {
        if (nextAnalysis) appendAnalysis(nextAnalysis);
        if (result?.quota) setQuota(result.quota);
        if (failure) setError(failure);
        if (!failure && nextAnalysis?.state === "completed" && nextSuggestions === null) {
          setError({ message: "Não foi possível validar a sugestão recebida." });
        }
      });
      if (!isProcessing(nextAnalysis) && ownsRequestKey()) {
        idempotencyKeyRef.current = null;
        if (!result?.quota) await reconcileAccess(requestToken);
      }
    } catch (caught) {
      if (caught?.code !== "request_cancelled" && ownsRequestKey()) {
        commit(requestToken, () => {
          setError(caught);
          if (caught?.status === 429 && quota)
            setQuota({ ...quota, remaining: 0 });
          if (caught?.code === "ai_access_disabled")
            setAccess((value) =>
              value ? { ...value, enabled: false } : value,
            );
        });
        if (caught?.retryable !== true) idempotencyKeyRef.current = null;
        await reconcileAccess(requestToken);
      }
    } finally {
      if (current(requestToken) && requestControllerRef.current === controller) {
        requestActiveRef.current = false;
        requestControllerRef.current = null;
        commit(requestToken, () => setSubmitting(false));
      }
    }
  };

  const changeUnit = (unitId) => {
    setSelectedUnits((ids) => {
      if (ids.includes(unitId)) return ids.filter((id) => id !== unitId);
      return ids.length < 3 ? [...ids, unitId] : ids;
    });
  };

  const result = outputOf(analysis);
  const validatedSuggestions = validSuggestions(analysis);
  const successfulCompleted = analysis?.state === "completed"
    && validatedSuggestions !== null;
  const suggestions = successfulCompleted ? validatedSuggestions : [];
  const compactHistory = history.filter(
    (item) => item?.analysisId !== analysis?.analysisId,
  );
  const revision =
    result.currentAppropriationRevision ||
    analysis?.currentAppropriationRevision ||
    null;
  const canFeedback = successfulCompleted && suggestions.length > 0;
  useEffect(() => {
    if (error || submitting || isProcessing(analysis) || suggestions.length > 0) {
      setExpanded(true);
    }
  }, [error, submitting, analysis?.state, analysis?.status, suggestions.length]);
  const sendFeedback = async () => {
    const requestToken = token();
    const analysisId = analysis?.analysisId;
    if (
      !current(requestToken) ||
      !canFeedback ||
      !analysisId ||
      feedbackAnalysisId === analysisId ||
      feedbackPendingRef.current?.analysisId === analysisId
    ) return;
    feedbackControllerRef.current?.abort();
    const controller = new AbortController();
    const pendingFeedback = { analysisId, controller };
    feedbackControllerRef.current = controller;
    feedbackPendingRef.current = pendingFeedback;
    commit(requestToken, () => setFeedbackPendingAnalysisId(analysisId));
    const feedbackIsCurrent = () =>
      feedbackControllerRef.current === controller &&
      feedbackPendingRef.current === pendingFeedback &&
      current(requestToken) &&
      currentAnalysisIdRef.current === analysisId;
    try {
      await submitAiFeedback(workId, analysisId, { helpful: true, comment: "" }, { signal: controller.signal });
      if (feedbackIsCurrent())
        commit(requestToken, () => setFeedbackAnalysisId(analysisId));
    } catch (caught) {
      if (caught?.code !== "request_cancelled" && feedbackIsCurrent())
        commit(requestToken, () => setError(caught));
    } finally {
      if (feedbackControllerRef.current === controller) feedbackControllerRef.current = null;
      if (feedbackPendingRef.current === pendingFeedback) {
        feedbackPendingRef.current = null;
        commit(requestToken, () => setFeedbackPendingAnalysisId(null));
      }
    }
  };

  const applySuggestion = (suggestion) => {
    if (
      !access?.enabled
      || readOnly
      || analysis?.state !== "completed"
      || !analysis?.analysisId
      || !validSuggestions(analysis)
    ) return;
    const key = `${analysis.analysisId}:${suggestion.suggestionId}`;
    if (applyGuardRef.current === key) return;
    applyGuardRef.current = key;
    onApply?.({ analysisId: analysis.analysisId, suggestionId: suggestion.suggestionId, currentAppropriationRevision: revision, lines: suggestion.lines });
  };

  return (
    <section
      className={`bill-reappropriation-ai-panel${
        expanded ? "" : " bill-reappropriation-ai-panel--collapsed"
      }`}
      aria-labelledby="bill-reappropriation-ai-title"
    >
      {/* Recolhido, o painel é um botão de ajuda: uma linha com o que a IA faz, a cota que
          resta e o gatilho. A cota fica sempre à vista porque é COMPARTILHADA com as outras
          telas da obra (Projeção, Desembolso): o cliente sabe o que tem antes de abrir. */}
      <header className="bill-reappropriation-ai-panel__header">
        <span className="bill-reappropriation-ai-panel__icon" aria-hidden="true">✦</span>
        <div className="bill-reappropriation-ai-panel__intro">
          <span className="item-ai-eyebrow">Ajuda da IA</span>
          <h3 id="bill-reappropriation-ai-title" ref={titleRef} tabIndex="-1">
            Encontrar destino com IA
          </h3>
          <p>
            Sugere o rateio pelo histórico da obra. Aplicar só preenche o
            rateio abaixo; salvar no Sienge continua sendo sua decisão.
          </p>
        </div>
        <div className="bill-reappropriation-ai-panel__aside">
          {!loading && access?.enabled ? (
            <span
              className={`bill-reappropriation-ai-panel__quota${
                exhausted ? " bill-reappropriation-ai-panel__quota--exhausted" : ""
              }`}
              title="A cota de IA é compartilhada com as outras telas da obra."
            >
              <strong>{quotaLabel(quota, access?.plan?.period)}</strong>
              <small>Cota compartilhada com as outras telas da obra</small>
            </span>
          ) : null}
          <button
            type="button"
            className={`button small ${expanded ? "button-ghost" : "button-outline"} bill-reappropriation-ai-panel__toggle`}
            aria-expanded={expanded}
            aria-controls={`bill-reappropriation-ai-content-${billId}`}
            aria-label={expanded ? "Ocultar sugestão da IA" : "Mostrar sugestão da IA"}
            onClick={() => setExpanded((value) => !value)}
          >
            {expanded ? "Ocultar" : "Pedir ajuda da IA"}
          </button>
        </div>
      </header>

      <div
        id={`bill-reappropriation-ai-content-${billId}`}
        className="bill-reappropriation-ai-panel__content"
        hidden={!expanded}
      >
      {loading ? (
        <p className="item-ai-loading" role="status">
          Preparando o título e o histórico…
        </p>
      ) : null}
      {error ? (
        <div className="item-ai-error" role="alert">
          <span>
            {error.message || "Não foi possível preparar a sugestão."}
          </span>
          <button type="button" onClick={load}>
            Tentar carregar novamente
          </button>
        </div>
      ) : null}
      {!loading && readOnly ? (
        <p className="item-ai-disabled" role="status">
          Este título está em modo somente leitura.
        </p>
      ) : null}
      {!loading && !readOnly && access?.enabled === false ? (
        <p className="item-ai-disabled" role="status">
          A IA não está habilitada para esta obra.
        </p>
      ) : null}

      {!loading && !readOnly && access?.enabled ? (
        <div className="bill-reappropriation-ai-panel__form">
          <p className="bill-reappropriation-ai-panel__quota-rule">
            Uma busca concluída usa uma cota; aplicar, editar e salvar não
            usam outra.
          </p>
          <fieldset>
            <legend>Unidades construtivas</legend>
            <small>
              Escolha de uma a três unidades que podem receber este rateio.
            </small>
            <div className="bill-reappropriation-ai-panel__units">
              {availableUnits.map((unit) => (
                <label key={unit.id}>
                  <input
                    type="checkbox"
                    checked={selectedUnits.includes(unit.id)}
                    onChange={() => changeUnit(unit.id)}
                    disabled={
                      !selectedUnits.includes(unit.id) &&
                      selectedUnits.length >= 3
                    }
                  />
                  {unit.description || `Unidade ${unit.id}`}
                </label>
              ))}
            </div>
          </fieldset>
          <fieldset className="bill-reappropriation-ai-panel__mode">
            <legend>Como apropriar</legend>
            <label>
              <input
                type="radio"
                name={`bill-ai-mode-${billId}`}
                checked={mode === "single"}
                onChange={() => setMode("single")}
              />{" "}
              Um único destino
            </label>
            <label>
              <input
                type="radio"
                name={`bill-ai-mode-${billId}`}
                checked={mode === "split"}
                onChange={() => setMode("split")}
              />{" "}
              Dividir o título
            </label>
          </fieldset>
          <label
            className="bill-reappropriation-ai-panel__question"
            htmlFor={`bill-ai-question-${billId}`}
          >
            Descreva o que este título representa
            <textarea
              id={`bill-ai-question-${billId}`}
              value={question}
              maxLength="1000"
              onChange={(event) => setQuestion(event.target.value)}
              placeholder="Ex.: locação de grua da torre durante a estrutura"
            />
            <small>{question.trim().length}/1.000 caracteres · mínimo 10</small>
          </label>
          <button
            type="button"
            className="bill-reappropriation-ai-panel__action"
            disabled={!canSubmit}
            onClick={submit}
          >
            {submitting ? "Buscando destinos…" : "Buscar destinos com IA"}
          </button>
          {exhausted ? (
            <p className="item-ai-upsell">
              Seu histórico continua disponível; a cota será renovada no próximo
              período.
            </p>
          ) : null}
        </div>
      ) : null}

      {isProcessing(analysis) ? (
        <div className="bill-reappropriation-ai-panel__processing">
          <p role="status">A sugestão está sendo preparada.</p>
          <button type="button" disabled={polling} onClick={refreshProcessing}>
            {polling ? "Atualizando…" : "Atualizar status da análise"}
          </button>
          {pollPaused ? (
            <small>
              A atualização automática foi pausada; atualize quando quiser sem
              usar nova cota.
            </small>
          ) : null}
        </div>
      ) : null}

      {suggestions.length ? (
        <section
          className="bill-reappropriation-ai-panel__plans"
          aria-label="Sugestões de rateio com IA"
        >
          <header>
            <span className="item-ai-eyebrow">Planos comparáveis</span>
            <h4>Destinos sugeridos</h4>
          </header>
          {suggestions.map((suggestion, index) => (
            <article
              className="bill-reappropriation-ai-plan"
              key={suggestion.suggestionId || `suggestion-${index}`}
              aria-labelledby={`bill-ai-plan-${billId}-${index}`}
            >
              <h5 id={`bill-ai-plan-${billId}-${index}`}>Plano {index + 1}</h5>
              <div className="bill-reappropriation-ai-plan__lines">
                {suggestion.lines?.slice(0, 10).map((line, lineIndex) => {
                  const unit = availableUnits.find(
                    (item) => item.id === line.buildingUnitId,
                  );
                  return (
                    <div
                      className="bill-reappropriation-ai-plan__line"
                      key={`${
                        line.candidateRef || line.wbsCode || "line"
                      }-${lineIndex}`}
                    >
                      <span className="bill-reappropriation-ai-plan__unit">
                        {unit?.description || `Unidade ${line.buildingUnitId}`}
                      </span>
                      <strong
                        aria-label={`Caminho EAP: ${
                          line.path || "indisponível"
                        }`}
                      >
                        {line.path || "Caminho EAP indisponível"}
                      </strong>
                      <span className="bill-reappropriation-ai-plan__percentage">
                        {percentage(line.percentageBps)}
                      </span>
                    </div>
                  );
                })}
              </div>
              <p>
                {suggestion.explanation ||
                  "A sugestão usa somente destinos EAP autorizados para as unidades escolhidas."}
              </p>
              <div className="bill-reappropriation-ai-plan__notes">
                <div>
                  <strong>Evidências</strong>
                  {listMessage(
                    suggestion.evidenceRefs,
                    "Nenhuma evidência adicional.",
                    "Evidências não disponibilizadas.",
                  ) || (
                    <ul>
                      {suggestion.evidenceRefs.map(
                        (evidence, evidenceIndex) => (
                          <li key={`${evidence}-${evidenceIndex}`}>
                            {evidence}
                          </li>
                        ),
                      )}
                    </ul>
                  )}
                </div>
                <div>
                  <strong>Limitações</strong>
                  {listMessage(
                    suggestion.limitations,
                    "Nenhuma limitação adicional.",
                    "Limitações não disponibilizadas.",
                  ) || (
                    <ul>
                      {suggestion.limitations.map(
                        (limitation, limitationIndex) => (
                          <li key={`${limitation}-${limitationIndex}`}>
                            {limitation}
                          </li>
                        ),
                      )}
                    </ul>
                  )}
                </div>
              </div>
              {!readOnly ? (
                <button
                  type="button"
                  disabled={!access?.enabled || applyGuardRef.current === `${analysis?.analysisId}:${suggestion.suggestionId}`}
                  onClick={() => applySuggestion(suggestion)}
                >
                  Aplicar sugestão ao rateio
                </button>
              ) : null}
            </article>
          ))}
        </section>
      ) : null}

      {successfulCompleted &&
      suggestions.length === 0 &&
      validatedSuggestions !== null ? (
        <p className="item-ai-provider-warning" role="status">
          A IA não encontrou um destino seguro. Detalhe o serviço ou altere as
          unidades escolhidas.
        </p>
      ) : null}
      {canFeedback ? (
        <div className="item-ai-feedback">
          <button
            type="button"
            disabled={
              feedbackAnalysisId === analysis.analysisId ||
              feedbackPendingAnalysisId === analysis.analysisId
            }
            onClick={sendFeedback}
          >
            Esta sugestão ajudou
          </button>
          {feedbackAnalysisId === analysis.analysisId ? <span>Obrigado pelo retorno.</span> : null}
        </div>
      ) : null}
      {compactHistory.length ? (
        <section className="bill-reappropriation-ai-panel__history">
          <h4>Análises anteriores</h4>
          <ul>
            {compactHistory.map((item) => (
              <li key={item.analysisId}>
                {item.createdAt
                  ? new Intl.DateTimeFormat("pt-BR", {
                      dateStyle: "short",
                    }).format(new Date(item.createdAt))
                  : "Data indisponível"}{" "}
                ·{" "}
                {historyLabel(item)}
              </li>
            ))}
          </ul>
        </section>
      ) : null}
      </div>
    </section>
  );
};

export default BillReappropriationAiPanel;
